import React, { FC, useEffect, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "@mui/material/Card";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LightTooltip from "@/components/LightTooltip";
import Tabs from "@mui/material/Tabs";
import TabPanel from "@/components/TabPanel";
import Tab from "@mui/material/Tab";
import { a11yProps } from "@/utils/a11yProps";
import { FormProvider, useForm } from "react-hook-form";
import { TabProgramGeneral } from "@/ui/content/management/programs/TabProgramGeneral";
import { TabProgramOUs } from "@/ui/content/management/programs/TabProgramOUs";
import { TabProgramConfiguration } from "@/ui/content/management/programs/TabProgramConfiguration";
import { TabProgramGeofence } from "@/ui/content/management/programs/TabProgramGeofence";
import createProgramSchema, {
  CreateProgramFormData,
} from "@/ui/content/management/programs/validation.schema";
import Button from "@/components/Button";
import dayjs from "dayjs";
import {
  CalculationMethodEnum,
  IncentiveStructureEnum,
  ProgramStatusEnum,
  ValidationRateEnum,
  YesNoEnum,
} from "@/domain/programs.enum";
import { useCreateProgram } from "@/hooks/management/usePrograms.manage";
import { ProgramPayload } from "@/domain/programs.interface";
import { useNavigate } from "react-router-dom";
import { getDateOnly } from "@/utils/dateUtils";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";

const CreateProgramPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("program");
  const { t: tCommon } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo, userHasAccessToCode } = useUserStore();
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const createAuditLog = useCreateAuditlog();
  const createProgram = useCreateProgram();
  const navigate = useNavigate();

  useEffect(() => {
    setPageTitle(t("title"));
  }, []);

  const [selectedTab, setSelectedTab] = useState(0);

  const methods = useForm<CreateProgramFormData>({
    resolver: yupResolver(createProgramSchema(tCommon)),
    mode: "all",
    defaultValues: {
      status: ProgramStatusEnum.ACTIVE,
      startDate: dayjs().format("YYYY-MM-DD"),
      orgUnitIds: [],
      cmRoles: [],
      additionalStaffRoles: [],
      weekdays: [],
      formFactors: [],
      events: [],
      assignCMCoordinator: "",
      enabledFormFactor2: false,
      mobilePhoneNumber: "",
      mobileDateOfBirth: "",
      minutesPingGeofenceValidationRequest: undefined,
      metersGeofenceAccuracyThreshold: undefined,
      metersRadiusGeofenceRequestValidation: undefined,
      requestGeofenceValidationsPerDay: undefined,
      minimumMinsGeofenceBetweenRequestValidations: undefined,
      minutesPingGeofenceAlive: undefined,
      minFormFactorConfidence: undefined,
      // validation request settings
      minutesValidationTimer: undefined,
      timerValidationRequestOpened: undefined,
      typeRequestValidation: "",
      distanceFilterGeofenceRequestValidation: undefined,
      desiredAccuracy: "",
      showGeofenceChip: false,
      automaticGeofenceTrigger: false,
      enableValidationRequestSms: false,
      minutesTimeToCompleteValidation: undefined,
      validationRequestMaxAttempts: undefined,
      enabledRewardFirstValidationSuccess: false,
      enabledRewardValidationRequestSuccess: false,
      enabledRewardOnDemandValidationSuccess: false,
      enableConsiderTimerOnMissedExpiration: true,
      enabledRewardNonCarriage: false,
      onDemandValidationRequest: false,
      onDemandValidationPeriodDays: undefined,
      onDemandValidationDays: [],
      // configuration
      enabledRewardUponAccountCreation: false,
      incentiveAccountCreation: undefined,
      incentiveFirstValidation: undefined,
      incentiveValidationRequest: undefined,
      incentiveOnDemandValidation: undefined,
      incentiveNonCarriage: undefined,
    },
  });

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: CreateProgramFormData) => {
    console.log("data", data);

    // Map form factor keys to display names
    const formFactorNameMap: Record<string, string> = {
      NYXOID_NASAL_SPRAY: "Nyxoid Nasal Spray",
      PEBBLE_NASAL_SPRAY: "Pebble Nasal Spray",
      PRENOXAD_INJECTABLE: "Prenoxad Injectable",
    };

    const payload: ProgramPayload = {
      // general information
      name: data.name as string,
      status: data.status as ProgramStatusEnum,
      roleId: data.cmRoles as string[],
      additionalStaffRoles: data.additionalStaffRoles as string[],

      // default values
      programType: "individual",
      createdBy: userInfo?._id,

      // program numbers
      maxParticipants: data.maxParticipants
        ? Number(data.maxParticipants)
        : undefined,
      duration: data.programDuration ? Number(data.programDuration) : undefined,
      budgetLimit: data.programBudgetLimit
        ? Number(data.programBudgetLimit)
        : undefined,

      parameters: {
        eligibilityConfirmation: data.eligibilityConfirmation === YesNoEnum.YES,
        consentForm: data.consentForm === YesNoEnum.YES,
        enableOnDemandIncentive: data.enableOnDemandIncentive === YesNoEnum.YES,
        minAmount: data.minAmount ? Number(data.minAmount) : undefined,
        maxAmount: data.maxAmount ? Number(data.maxAmount) : undefined,
        maxEarnings: data.maxEarnings ? Number(data.maxEarnings) : undefined,
        incentiveStructure: data.incentiveStructure as IncentiveStructureEnum,
        startDate: getDateOnly(data.startDate as dayjs.Dayjs | null) || "",
        endDate: data.endDate
          ? getDateOnly(data.endDate as dayjs.Dayjs | null) || ""
          : undefined,
        programDays: data.programDays ? Number(data.programDays) : undefined,
      },

      // program ous
      orgUnitIds: data.orgUnitIds as string[],

      configuration: {
        incentiveLadder: {
          calculationMethod: data.calculationMethod as CalculationMethodEnum,
          basedIncentiveNegativeResults: Number(
            data.basedIncentiveNegativeResults,
          ),
          basedIncentivePositiveResults: Number(
            data.basedIncentivePositiveResults,
          ),
          incentiveAccountCreation:
            data.incentiveAccountCreation !== undefined &&
            data.incentiveAccountCreation !== null &&
            data.incentiveAccountCreation !== ""
              ? Number(data.incentiveAccountCreation)
              : undefined,
          incentiveFirstValidation:
            data.incentiveFirstValidation !== undefined &&
            data.incentiveFirstValidation !== null &&
            data.incentiveFirstValidation !== ""
              ? Number(data.incentiveFirstValidation)
              : undefined,
          incentiveValidationRequest:
            data.incentiveValidationRequest !== undefined &&
            data.incentiveValidationRequest !== null &&
            data.incentiveValidationRequest !== ""
              ? Number(data.incentiveValidationRequest)
              : undefined,
          incentiveOnDemandValidation:
            data.incentiveOnDemandValidation !== undefined &&
            data.incentiveOnDemandValidation !== null &&
            data.incentiveOnDemandValidation !== ""
              ? Number(data.incentiveOnDemandValidation)
              : undefined,
          incentiveNonCarriage:
            data.incentiveNonCarriage !== undefined &&
            data.incentiveNonCarriage !== null &&
            data.incentiveNonCarriage !== ""
              ? Number(data.incentiveNonCarriage)
              : undefined,
          incrementValueNegative: data.incrementValueNegative
            ? Number(data.incrementValueNegative)
            : undefined,
          maxIncentiveNegativeRe: data.maxIncentiveNegativeRe
            ? Number(data.maxIncentiveNegativeRe)
            : undefined,
          resetAmount: data.resetAmount ? Number(data.resetAmount) : undefined,
        },
        validationLimits: {
          timeSubmitValidation: Number(data.timeToSubmitValidation),
          validationTimeReminder: data.validationLimitReminder
            ? Number(data.validationLimitReminder)
            : undefined,
        },
        validationRates: {
          needsValidation: true,
          validationRates: data.validationRates as ValidationRateEnum,
          weekdays: data.weekdays as string[],
        },
        contact: {
          name: data.fullName ? (data.fullName as string) : undefined,
          email: data.email ? (data.email as string) : undefined,
          phoneNumber: data.phoneNumber
            ? (data.phoneNumber as string)
            : undefined,
        },
        reenrollmentDays: data.numberOfDays
          ? Number(data.numberOfDays)
          : undefined,
        formFactors:
          Array.isArray(data.formFactors) && data.formFactors.length > 0
            ? (data.formFactors as string[]).map((factor) => ({
                type: factor,
                name: formFactorNameMap[factor] || factor,
              }))
            : undefined,
        assignCMCoordinator: (data.assignCMCoordinator as any) || undefined,
        enabledFormFactor2: (data.enabledFormFactor2 as boolean) ?? false,
        mobilePhoneNumber: (data.mobilePhoneNumber as any) || undefined,
        mobileDateOfBirth: (data.mobileDateOfBirth as any) || undefined,
        validationRequestSettings: {
          minutesValidationTimer: data.minutesValidationTimer
            ? Number(data.minutesValidationTimer)
            : undefined,
          timerValidationRequestOpened: data.timerValidationRequestOpened
            ? Number(data.timerValidationRequestOpened)
            : undefined,
          typeRequestValidation:
            (data.typeRequestValidation as string) || undefined,
          distanceFilterGeofenceRequestValidation:
            data.distanceFilterGeofenceRequestValidation
              ? Number(data.distanceFilterGeofenceRequestValidation)
              : undefined,
          desiredAccuracy: (data.desiredAccuracy as string) || undefined,
          showGeofenceChip: (data.showGeofenceChip as boolean) ?? false,
          automaticGeofenceTrigger:
            (data.automaticGeofenceTrigger as boolean) ?? false,
          enableValidationRequestSms:
            (data.enableValidationRequestSms as boolean) ?? false,
          minutesTimeToCompleteValidation: data.minutesTimeToCompleteValidation
            ? Number(data.minutesTimeToCompleteValidation)
            : undefined,
          validationRequestMaxAttempts: data.validationRequestMaxAttempts
            ? Number(data.validationRequestMaxAttempts)
            : undefined,
          enabledRewardFirstValidationSuccess:
            (data.enabledRewardFirstValidationSuccess as boolean) ?? false,
          enabledRewardValidationRequestSuccess:
            (data.enabledRewardValidationRequestSuccess as boolean) ?? false,
          enabledRewardOnDemandValidationSuccess:
            (data.enabledRewardOnDemandValidationSuccess as boolean) ?? false,
          enableConsiderTimerOnMissedExpiration:
            (data.enableConsiderTimerOnMissedExpiration as boolean) ?? true,
          enabledRewardNonCarriage:
            (data.enabledRewardNonCarriage as boolean) ?? false,
          onDemandValidationRequest:
            (data.onDemandValidationRequest as boolean) ?? false,
          onDemandValidationPeriodDays: data.onDemandValidationPeriodDays
            ? Number(data.onDemandValidationPeriodDays)
            : undefined,
          onDemandValidationDays: data.onDemandValidationDays as number[],
          minutesPingGeofenceValidationRequest:
            data.minutesPingGeofenceValidationRequest
              ? Number(data.minutesPingGeofenceValidationRequest)
              : undefined,
          metersGeofenceAccuracyThreshold: data.metersGeofenceAccuracyThreshold
            ? Number(data.metersGeofenceAccuracyThreshold)
            : undefined,
          metersRadiusGeofenceRequestValidation:
            data.metersRadiusGeofenceRequestValidation
              ? Number(data.metersRadiusGeofenceRequestValidation)
              : undefined,
          requestGeofenceValidationsPerDay:
            data.requestGeofenceValidationsPerDay
              ? Number(data.requestGeofenceValidationsPerDay)
              : undefined,
          minimumMinsGeofenceBetweenRequestValidations:
            data.minimumMinsGeofenceBetweenRequestValidations
              ? Number(data.minimumMinsGeofenceBetweenRequestValidations)
              : undefined,
          minutesPingGeofenceAlive: data.minutesPingGeofenceAlive
            ? Number(data.minutesPingGeofenceAlive)
            : undefined,
          stickyAndroidNotificationTitle:
            (data.stickyAndroidNotificationTitle as string) || undefined,
          stickyAndroidNotificationText:
            (data.stickyAndroidNotificationText as string) || undefined,
          minFormFactorConfidence:
            data.minFormFactorConfidence !== undefined &&
            data.minFormFactorConfidence !== null &&
            data.minFormFactorConfidence !== ""
              ? Number(data.minFormFactorConfidence)
              : undefined,
        },
        enabledRewardUponAccountCreation:
          (data.enabledRewardUponAccountCreation as boolean) ?? false,
        educationalMaterials: data.educationalMaterials as string[],
      },

      // events - exclude eventId for creation
      events:
        (data.events as any)?.length > 0
          ? (data.events as any).map(({ eventId, ...event }: any) => event)
          : undefined,
    };

    await createProgram.mutateAsync(payload, {
      onSuccess: () => {
        showSnackBar(t("messages.add-success"), "success");

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.PROGRAM,
          option: OPTIONS.PROGRAM_ADD,
          actionCode: ACTION_CODE.WEB_PROG_ADD,
          action: "When adding a new program (adding)",
          detail: `New program added ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
        navigate("/programs");
      },
      onError: () => {
        showSnackBar(t("messages.add-error"), "error");
      },
    });
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: t("page-title"),
            href: "/programs",
            isLink: true,
          },
          {
            label: t("breadcrumb.configuration"),
            href: "/programs/create",
            isLink: true,
          },
        ]}
      />

      <Box
        mt={1}
        mb={4}
        sx={{
          width: "100%",
        }}
        display="flex"
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography
          color={theme.palette.textMain.dark2}
          variant="headlineSmall"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {t("page-create")}

          <LightTooltip title={t("messages.information")} arrow theme={theme}>
            <InfoOutlinedIcon
              sx={{
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <FormProvider {...methods}>
            <Box sx={{ width: "100%", minHeight: 380 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="invite user tabs"
                >
                  <Tab label={t("tabs.cm-program")} {...a11yProps(0)} />
                  <Tab label={t("tabs.program-ous")} {...a11yProps(1)} />
                  <Tab label={t("tabs.configuration")} {...a11yProps(2)} />
                  <Tab
                    label={t("tabs.geofence", "Geofence & Validation")}
                    {...a11yProps(3)}
                  />
                </Tabs>
              </Box>
              <TabPanel value={selectedTab} index={0}>
                <TabProgramGeneral />
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <TabProgramOUs />
              </TabPanel>
              <TabPanel value={selectedTab} index={2}>
                <TabProgramConfiguration />
              </TabPanel>
              <TabPanel value={selectedTab} index={3}>
                <TabProgramGeofence />
              </TabPanel>
            </Box>
          </FormProvider>
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variantType="cancel"
              onClick={() => navigate("/programs")}
              disabled={false}
              label={t("buttons.cancel")}
            />
            <Button
              variantType="save"
              onClick={async () => {
                if (!userHasAccessToCode("PROG_ADD")) {
                  setShowPermissionDeniedModal(true);
                  return;
                }

                const result = await methods.trigger();
                if (!result) {
                  const errors = methods.formState.errors;
                  console.log("Validation errors:", errors);

                  // Find first error and show it
                  const firstErrorKey = Object.keys(errors)[0];
                  const firstError =
                    errors[firstErrorKey as keyof typeof errors];
                  const errorMessage =
                    firstError?.message || "Please check all required fields";

                  showSnackBar(errorMessage, "error");
                  return;
                }

                methods.handleSubmit(onSubmit)();
              }}
              disabled={createProgram.isPending}
              isLoading={createProgram.isPending}
              label={t("buttons.create")}
            />
          </Box>
        </CardContent>
      </Card>
      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </Box>
  );
};

export default CreateProgramPage;
